import React, { useState, useEffect } from "react";
import { Col, Container, Row, Form, Button } from "react-bootstrap";
import AdministrationIcon from "../assets/images/two_gears_rgb_red.svg";
import "../assets/styles/newScope.scss";
import NewScopeTable from "../components/NewScope/NewScopeTable";
import RequiredLabel from "../components/Common/RequiredLabel";
import { getAllScopes, createScope } from "../services/scopeApi";
import PaginationComponent from "../components/PaginationComponent";
import NewScopeButtons from "../components/NewScope/NewScopeButtons";

// Slowniki (stale — identyczne z backendem NewScopeConstants)
const sourceTypes = [
  { value: 1, label: "Średnia" },
  { value: 2, label: "Mediana" },
];

const NewScopeForm = () => {
  const [scopes, setScopes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const [sortConfig, setSortConfig] = useState({
    key: "PlannedDate",
    direction: "desc",
  });

  const [pagination, setPagination] = useState({
    itemsCount: 0,
    pageNumber: 1,
    pageSize: 20,
    totalPages: 0,
  });

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  const [formData, setFormData] = useState({
    sourceType: "",
    fromMinutes: "",
    toMinutes: "",
    plannedDate: tomorrow.toISOString().split("T")[0],
  });

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadScopes();
  }, [currentPage, itemsPerPage, sortConfig]);

  const loadScopes = async () => {
    try {
      const response = await getAllScopes(
        currentPage,
        itemsPerPage,
        sortConfig.key,
        sortConfig.direction,
      );

      setScopes(response.items ?? []);

      setPagination({
        itemsCount: response.itemsCount ?? 0,
        pageNumber: response.pageNumber ?? 1,
        pageSize: response.pageSize ?? itemsPerPage,
        totalPages: response.totalPages ?? 0,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      sourceType: "",
      fromMinutes: "",
      toMinutes: "",
      plannedDate: tomorrow.toISOString().split("T")[0],
    });
  };

  const handleSortChange = (key) => {
    setCurrentPage(1);

    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  // Wysłanie formularza
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.sourceType) {
      alert("Wybierz typ przedziału.");
      return;
    }

    if (!formData.plannedDate) {
      alert("Podaj planowaną datę zmiany.");
      return;
    }

    const from = Number(formData.fromMinutes);
    const to = Number(formData.toMinutes);

    if (!Number.isInteger(from) || from < 0) {
      alert("Przedział od musi być nieujemną liczbą całkowitą.");
      return;
    }

    if (!Number.isInteger(to) || to < 0) {
      alert("Przedział do musi być nieujemną liczbą całkowitą.");
      return;
    }

    if (from > to) {
      alert("Przedział od nie może być większy od przedziału do.");
      return;
    }

    setSubmitting(true);

    try {
      await createScope({
        sourceType: Number(formData.sourceType),
        fromMinutes: from,
        toMinutes: to,
        plannedDate: formData.plannedDate,
      });

      await loadScopes();
      alert("Nowy przedział został dodany.");

      resetForm();
    } catch (error) {
      if (error.response?.status === 423) {
        alert(
          "Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana",
        );
        return;
      }

      const msg =
        error.response?.data?.data?.message ||
        "Wystąpił błąd podczas zapisywania przedziału.";
      alert(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="new-scope-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <Col xs="auto">
            <img
              src={AdministrationIcon}
              alt="Ikona nowego przedziału"
              style={{ width: "30px", height: "30px" }}
            />
          </Col>
          <Col>
            <h3 style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
              {"Dodaj nowy przedział"}
            </h3>
          </Col>
          <Col xs="auto">
            <NewScopeButtons />
          </Col>
        </Row>

        <Row>
          {/* Lewa kolumna — formularz (4/12) */}
          <Col md={4}>
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-2" controlId="sourceType">
                <RequiredLabel>
                  Wybierz z jakich danych ma być tworzony przedział
                </RequiredLabel>
                <Form.Select
                  required
                  value={formData.sourceType}
                  onChange={(e) => handleChange("sourceType", e.target.value)}
                >
                  <option value="">-- Wybierz --</option>

                  {sourceTypes.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-2" controlId="fromMinutes">
                <RequiredLabel>Przedział od [min]</RequiredLabel>

                <Form.Control
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={formData.fromMinutes}
                  onChange={(e) => handleChange("fromMinutes", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="toMinutes">
                <RequiredLabel>Przedział do [min]</RequiredLabel>
                <Form.Control
                  required
                  type="number"
                  min="0"
                  step="1"
                  value={formData.toMinutes}
                  onChange={(e) => handleChange("toMinutes", e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-2" controlId="plannedDate">
                <RequiredLabel>Planowana data zmiany</RequiredLabel>
                <Form.Control
                  required
                  type="date"
                  value={formData.plannedDate}
                  onChange={(e) => handleChange("plannedDate", e.target.value)}
                />
              </Form.Group>

              <div className="d-flex gap-2 mt-3">
                <Button
                  variant="secondary"
                  type="button"
                  onClick={resetForm}
                  disabled={submitting}
                >
                  Reset
                </Button>

                <Button variant="primary" type="submit" disabled={submitting}>
                  {submitting ? "Zapisywanie..." : "Dodaj przedział"}
                </Button>
              </div>
            </Form>
          </Col>

          {/* Prawa kolumna — lista scopów */}
          <Col md={8}>
            <NewScopeTable
              data={scopes}
              sortConfig={sortConfig}
              onSortChange={handleSortChange}
            />

            <div className="mt-3">
              <PaginationComponent
                itemsPerPage={pagination.pageSize}
                dataCount={scopes.length}
                itemsCount={pagination.itemsCount}
                pageNumber={pagination.pageNumber}
                totalPages={pagination.totalPages}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(size) => {
                  setItemsPerPage(size);
                  setCurrentPage(1);
                }}
                itemsPerPageOptions={[20, 100, 500]}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default NewScopeForm;
